import { ChevronDown } from '@carbon/icons-react';
import { FormControl, MenuItem, Select, TextField } from '@mui/material';
import { useEffect, useRef, useState } from 'react';
import useStyles from './styles';
import { useTranslation } from 'react-i18next';

type Props = {
    options: any[];
    value: string;
    onChange: (event?: any, item?: any) => void;
    onBlur?: (any) => void;
    id: string;
    isDisabled?: boolean;
    isEmpty?: boolean;
    isFilterable?: boolean;
    dataTestId: string;
    labelText?: string;
    nAplaceholder?: (any) => void;
    errorText?: string;
    menuPaperStyle: any;
    width?: string;
};

const CustomSelect = ({
    options,
    value,
    onChange,
    onBlur,
    id,
    isDisabled,
    isEmpty,
    isFilterable,
    dataTestId,
    labelText,
    nAplaceholder,
    errorText,
    menuPaperStyle,
    width,
}: Props) => {
    const { t } = useTranslation();
    const classes = useStyles();
    const nothingSelected = '-1';
    const [filter, setFilter] = useState('');
    const [open, setOpen] = useState(false);
    const filterInputRef = useRef(null);

    const handleOpen = () => {
        setOpen(true);
    };

    const handleClose = () => {
        if (filter) return;
        setOpen(false);
    };

    const handleFilterChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setFilter(event.target.value.toLowerCase());
    };

    const filteredOptions = options?.filter((option) => option.label.toLowerCase().includes(filter));

    const alphanumericSort = (a, b) => {
        // Regular expressions to find the numeric parts and non-numeric parts of the labels
        const reA = /[^a-zA-Z]+/g;
        const reN = /[^0-9]+/g;

        // Extract the alphabetic and numeric parts of the labels
        const aParts = a.label.split(reA);
        const bParts = b.label.split(reA);
        const aAlpha = aParts.filter((part) => isNaN(parseInt(part)));
        const bAlpha = bParts.filter((part) => isNaN(parseInt(part)));
        const aNum = aParts.filter((part) => !isNaN(parseInt(part))).map((part) => parseInt(part, 10));
        const bNum = bParts.filter((part) => !isNaN(parseInt(part))).map((part) => parseInt(part, 10));

        // Compare the alphabetic parts
        for (let i = 0; i < Math.min(aAlpha.length, bAlpha.length); i++) {
            const comp = aAlpha[i].localeCompare(bAlpha[i]);
            if (comp !== 0) return comp;
        }

        // If alphabetic parts are the same, compare the numeric parts
        for (let i = 0; i < Math.min(aNum.length, bNum.length); i++) {
            if (aNum[i] !== bNum[i]) {
                return aNum[i] - bNum[i];
            }
        }

        // If one label has more numeric parts than the other, the shorter one comes first
        if (aNum.length !== bNum.length) {
            return aNum.length - bNum.length;
        }

        // If both labels have the same alphabetic and numeric parts, they are equal
        return 0;
    };

    const sortedFilteredOptions = filteredOptions?.sort(alphanumericSort);

    const onChangeAndResetFilter = (event: any) => {
        event.stopPropagation();
        setFilter(''); // Reset the filter
        onChange(event); // Call the provided onChange handler
        if (open && filterInputRef.current) {
            // Attempt to refocus only if the dropdown remains open
            filterInputRef.current.focus();
        }
    };

    const handleTextFieldClick = (event: any) => {
        event.stopPropagation();
    };

    useEffect(() => {
        // Focus management now strictly tied to 'open' state to prevent unnecessary focus attempts
        if (open && filterInputRef.current) {
            filterInputRef.current.focus();
        }
    }, [open]);

    return (
        <div style={{ width: width ? width : '100%' }}>
            {labelText && (
                <label className={isEmpty ? `${classes.label} ${classes.errorColor}` : classes.label} htmlFor={id}>
                    {labelText}
                </label>
            )}
            <FormControl className={classes.selectBox}>
                <Select
                    data-testid={dataTestId}
                    value={value}
                    onChange={onChangeAndResetFilter}
                    onBlur={onBlur}
                    inputProps={{
                        name: id,
                        id: id,
                        style: {
                            paddingLeft: 8,
                            height: '32px !important',
                        },
                    }}
                    IconComponent={ChevronDown}
                    MenuProps={{
                        PaperProps: { style: menuPaperStyle },
                        MenuListProps: {
                            // Prevent the menu from closing when clicking inside the menu
                            onClick: handleTextFieldClick,
                        },
                        // add so that drowdown is always underneath the input field
                        anchorOrigin: {
                            vertical: 'bottom',
                            horizontal: 'left',
                        },
                        transformOrigin: {
                            vertical: 'top',
                            horizontal: 'left',
                        },
                    }}
                    disabled={isDisabled}
                    className={`${isDisabled ? classes.disabled : classes.select} ${isEmpty && classes.errorColor}`}
                    onOpen={handleOpen}
                    onClose={handleClose}
                >
                    {isFilterable && (
                        <MenuItem>
                            <TextField
                                ref={filterInputRef}
                                value={filter}
                                onClick={handleTextFieldClick}
                                onChange={handleFilterChange}
                                onKeyDown={handleTextFieldClick}
                                fullWidth
                                placeholder={t('COMMON.FILTER_DROPDOWN_TEXT')}
                            />
                        </MenuItem>
                    )}

                    {nAplaceholder && (
                        <MenuItem value={nothingSelected} className={classes.dropdownElem}>
                            {nAplaceholder?.toString()}
                        </MenuItem>
                    )}
                    {sortedFilteredOptions?.map((option, index) => (
                        <MenuItem key={index} value={option.value} className={classes.dropdownElem}>
                            {option.label}
                        </MenuItem>
                    ))}
                </Select>
            </FormControl>
            {!isEmpty && (
                <div className={classes.errorWrapper}>
                    <span className={classes.errorSpacer} />
                </div>
            )}
            {isEmpty && (
                <div className={classes.errorWrapper}>
                    <div className={classes.label} />
                    <div className={[classes.value, classes.error].join(' ')}>{errorText}</div>
                </div>
            )}
        </div>
    );
};

export default CustomSelect;
